import { Flex, Paper, Text, Textarea, UnstyledButton } from '@mantine/core';
import { ArrowRightIcon, ArrowUpIcon, StopIcon } from '@phosphor-icons/react';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';

import { pluralize } from 'lib/utils';
import TRACKING_IDS from 'tracking-ids';

import classes from '../chat.module.css';
import {
  CHAT_INPUT_COUNT_THRESHOLD,
  CHAT_INPUT_ICON_SIZE,
  CHAT_INPUT_MAX_LENGTH,
} from '../constants';

import type { ChatInputProps } from '../types';

// The default composer's send and stop icons.
const DEFAULT_ICON_SIZE = 16;

const ChatInput = forwardRef<HTMLTextAreaElement, ChatInputProps>(
  (
    {
      onSubmit,
      onStop,
      initialInput = '',
      value,
      onInputChange,
      loading = false,
      disabled = false,
      location = 'launchpad',
      placeholder = 'What do you need to know?',
      promptSource,
      footerSlot,
      headerSlot,
      variant = 'default',
    }: ChatInputProps,
    ref,
  ) => {
    const [internalInput, setInternalInput] = useState<string>(initialInput);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    // Merge external ref with internal ref
    useImperativeHandle(ref, () => inputRef.current!);

    const SendIcon = location === 'launchpad' ? ArrowRightIcon : ArrowUpIcon;
    // The clean composer (Chat UI Clean) adds the length limit, a taller box
    // and 24px light icons; the default keeps the composer as it was.
    const isClean = variant === 'clean';

    const isControlled = value !== undefined;
    const input = isControlled ? value : internalInput;
    const isEmpty = !input.trim();
    const overLimitBy = input.length - CHAT_INPUT_MAX_LENGTH;
    const isOverLimit = isClean && overLimitBy > 0;
    const showCount =
      isClean &&
      input.length >= CHAT_INPUT_MAX_LENGTH * CHAT_INPUT_COUNT_THRESHOLD;
    const canSend = !isEmpty && !isOverLimit && !loading && !disabled;

    const handlePaperClick = () => {
      inputRef.current?.focus();
    };

    const clearInputState = () => {
      if (isControlled) {
        onInputChange?.('');
      } else {
        setInternalInput('');
      }
    };

    const onKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (isEmpty && e.key === 'Enter') {
        e.preventDefault();
        return;
      }

      if (e.key !== 'Enter' || e.shiftKey) return;

      e.preventDefault();

      if (!canSend) return;

      onSubmit(input);
      clearInputState();
    };

    const handleInputChange = (newValue: string) => {
      if (isControlled) {
        onInputChange?.(newValue);
      } else {
        setInternalInput(newValue);
      }
    };

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (canSend) {
        onSubmit(input);
        clearInputState();
      }
    };

    return (
      <>
        <Paper
          onClick={handlePaperClick}
          className={classes.chatInput}
          data-testid="hatch-chat-input"
          data-disabled={disabled || undefined}
          data-variant={variant}
          miw={'100%'}
          w={'100%'}
        >
          <form onSubmit={handleSubmit} className={classes.chatInputForm}>
            <Flex direction="column" gap="xs" h="100%">
              {headerSlot}
              <Textarea
                classNames={{ input: classes.textarea }}
                autosize
                maxRows={isClean ? 7 : 3}
                ref={inputRef}
                value={input}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyDown={onKeyPress}
                placeholder={placeholder}
                variant="unstyled"
                disabled={disabled}
              />

              <Flex
                justify="space-between"
                align="center"
                gap={8}
                mt="auto"
                style={{ flexShrink: 0 }}
              >
                <Flex align="center" gap={isClean ? 'sm' : 8}>
                  {footerSlot}
                </Flex>
                <Flex align="center" gap={8} ml="auto">
                  {showCount && (
                    <Text
                      fz="var(--klue-chat-input-note-size)"
                      c={isOverLimit ? 'red.7' : 'gray.5'}
                      className={classes.count}
                    >
                      {input.length.toLocaleString()} /{' '}
                      {CHAT_INPUT_MAX_LENGTH.toLocaleString()}
                    </Text>
                  )}
                  {/* UnstyledButton, not ActionIcon: nectar layers a colour
                    variant onto ActionIcon whose disabled rule (0,4,0, with
                    opacity 0.25) would repaint the empty state. */}
                  <UnstyledButton
                    type={loading ? 'button' : 'submit'}
                    onClick={loading ? () => onStop?.() : undefined}
                    className={classes.submit}
                    aria-label={loading ? 'Stop' : 'Send'}
                    data-stopping={loading || undefined}
                    data-over-limit={isOverLimit || undefined}
                    disabled={(!loading && !canSend) || disabled}
                    style={{ transition: 'background-color 0.1s ease-out' }}
                    data-tracking-id={
                      location === 'launchpad'
                        ? TRACKING_IDS.hatch.chat.submit.launchpad
                        : TRACKING_IDS.hatch.chat.submit.followup
                    }
                    data-prompt-source={promptSource ?? 'organic'}
                  >
                    {loading ? (
                      <StopIcon
                        weight={isClean ? 'fill' : 'bold'}
                        size={
                          isClean ? CHAT_INPUT_ICON_SIZE : DEFAULT_ICON_SIZE
                        }
                      />
                    ) : (
                      <SendIcon
                        weight={isClean ? 'light' : 'bold'}
                        size={
                          isClean ? CHAT_INPUT_ICON_SIZE : DEFAULT_ICON_SIZE
                        }
                      />
                    )}
                  </UnstyledButton>
                </Flex>
              </Flex>
            </Flex>
          </form>
        </Paper>
        {isOverLimit && (
          <Text
            px="md"
            fz="var(--klue-chat-input-note-size)"
            c="red.7"
            role="status"
          >
            Too long to send. Shorten your message by{' '}
            {overLimitBy.toLocaleString()} {pluralize('character', overLimitBy)}
            .
          </Text>
        )}
      </>
    );
  },
);

ChatInput.displayName = 'ChatInput';

export type ChatInputHandle = HTMLTextAreaElement;
export default ChatInput;
